import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Twitter, Linkedin, Instagram, Youtube, Mail, MapPin } from 'lucide-react';
import { NofeeNavLogo } from './NoFeeLogo';

const SUPPORT_EMAIL = 'support@nofee.restaurant';

/**
 * FooterSection — site-wide footer. Fully translated via react-i18next so
 * every column header and link label flips when the user picks a language
 * in the navbar switcher.
 *
 * All link labels live under `footer.links.*` in `/i18n/locales/en.json`
 * and are propagated to the 13 other languages by `build_locales.py`.
 */
const FooterSection = () => {
  const { t } = useTranslation();

  const columns = [
    {
      title: t('footer.product', 'Product'),
      links: [
        { label: t('footer.links.product_for_restaurants', 'For Restaurants'), href: '/restaurants' },
        { label: t('footer.links.product_for_customers',   'For Customers'),   href: '/customers' },
        { label: t('footer.links.product_ordering',        'NoFee Ordering'),  href: '/ordering' },
        { label: t('footer.links.product_rewards',         'NoFee Rewards'),   href: '/rewards' },
        { label: t('footer.links.product_pricing',         'Pricing'),         href: '/pricing' },
      ],
    },
    {
      title: t('footer.company', 'Company'),
      links: [
        { label: t('footer.links.company_about',   'About Us'), href: '/about' },
        { label: t('footer.links.company_why',     'Why NoFee'), href: '/for-restaurants/why-nofee' },
      ],
    },
    {
      title: t('footer.support', 'Support'),
      links: [
        { label: t('footer.links.support_help',    'Help Center'),   href: '/help' },
        { label: t('footer.links.support_contact', 'Contact Us'),    href: '/contact' },
        { label: t('footer.links.support_email',   SUPPORT_EMAIL),   href: `mailto:${SUPPORT_EMAIL}` },
      ],
    },
    {
      title: t('footer.legal', 'Legal'),
      links: [
        { label: t('footer.links.legal_terms',   'Terms of Service'), href: '/terms' },
        { label: t('footer.links.legal_privacy', 'Privacy Policy'),   href: '/privacy' },
        { label: t('footer.links.legal_cookies', 'Cookie Policy'),    href: '/cookies' },
      ],
    },
  ];

  return (
    <footer className="bg-black pt-20 pb-8 border-t border-gold-900/20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-10 mb-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="mb-4">
              <NofeeNavLogo height={36} />
            </div>
            <p className="text-white/40 text-sm leading-relaxed max-w-sm mb-6">
              {t('footer.description', '0% commission food ordering platform. Built for restaurants. Powered by fairness.')}
            </p>

            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-2 px-3 py-2 mb-4 rounded-xl bg-gold-500/10 border border-gold-500/30 hover:border-gold-500 hover:bg-gold-500/20 transition-all group"
              data-testid="footer-support-email"
            >
              <Mail className="w-4 h-4 text-gold-400" />
              <span className="text-sm font-semibold text-gold-300 group-hover:text-gold-200">
                {SUPPORT_EMAIL}
              </span>
            </a>

            <div className="flex items-start gap-2 text-white/40 text-xs mb-6 max-w-xs">
              <MapPin className="w-4 h-4 text-gold-400 shrink-0 mt-0.5" />
              <span>
                <strong className="text-white/60">NoFee LLC</strong>
                <br />
                {t('footer.address_line1', '9100 Conroy Road, Suite 200')}
                <br />
                {t('footer.address_line2', 'Windermere, FL 34786 · USA')}
              </span>
            </div>

            <div className="flex gap-2">
              {[Twitter, Linkedin, Instagram, Youtube].map((Icon, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={t('footer.follow_us', 'Follow us')}
                  className="w-9 h-9 bg-white/5 hover:bg-gold-500 border border-gold-900/20 hover:border-gold-500 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 group"
                >
                  <Icon className="w-4 h-4 text-white/40 group-hover:text-black" />
                </button>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {columns.map((section, i) => (
            <div key={i} data-testid={`footer-col-${i}`}>
              <h4 className="text-gold-400 font-semibold mb-4 text-xs uppercase tracking-wider">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((item, li) => {
                  const isExternal = item.href.startsWith('mailto:') || item.href.startsWith('http');
                  return (
                    <li key={li}>
                      <a
                        href={item.href}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        className="text-white/40 hover:text-gold-400 text-sm transition-colors"
                        data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {item.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="border-t border-gold-900/20 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-sm">
            {t('footer.copyright', { year: new Date().getFullYear(), defaultValue: `© ${new Date().getFullYear()} NoFee Restaurant LLC. All rights reserved.` })}
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-white/30 text-sm">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex items-center gap-1.5 hover:text-gold-400 transition-colors"
            >
              <Mail className="w-4 h-4" /> {SUPPORT_EMAIL}
            </a>
            <span className="flex items-center gap-1.5">
              <Globe className="w-4 h-4" /> nofee.restaurant
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
